// Freehand shape recognition for the Holographic Garden Planner.
// You sketch a loose loop; we detect circle vs rectangle and snap it to a clean
// shape. All output is normalized 0..1 relative to the canvas so it renders
// identically on any screen size (web + mobile).

function bbox(points) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

function centroid(points) {
  let sx = 0, sy = 0;
  for (const p of points) { sx += p.x; sy += p.y; }
  return { x: sx / points.length, y: sy / points.length };
}

// Circularity: how evenly the points sit around their centre (low = circular).
function circularity(points, c) {
  const radii = points.map((p) => Math.hypot(p.x - c.x, p.y - c.y));
  const mean = radii.reduce((a, b) => a + b, 0) / radii.length;
  if (mean === 0) return 1;
  const variance = radii.reduce((a, r) => a + (r - mean) ** 2, 0) / radii.length;
  return Math.sqrt(variance) / mean;
}

// Recognize a freehand stroke → clean shape.
// points: [{x,y}] in canvas PIXELS. canvasW/H: canvas pixel size.
// Returns null for tiny scribbles, else a normalized layout {shape,x,y,w,h}.
export function recognizeShape(points, canvasW, canvasH) {
  if (!points || points.length < 4 || !canvasW || !canvasH) return null;
  const box = bbox(points);
  // Ignore accidental taps / tiny strokes.
  if (box.w < canvasW * 0.06 && box.h < canvasH * 0.06) return null;

  const c = centroid(points);
  const circ = circularity(points, c);
  const aspect = box.w / Math.max(box.h, 1);
  const roundish = circ < 0.20 && aspect > 0.72 && aspect < 1.38;
  const padF = 0.06; // fraction of the drawn box added as breathing room

  if (roundish) {
    // True circle: use equal PIXEL diameter, then normalize per-axis so it stays round.
    let dPx = Math.max(box.w, box.h) * (1 + padF * 2);
    dPx = Math.min(dPx, Math.min(canvasW, canvasH) * 0.82);
    const cxPx = box.minX + box.w / 2;
    const cyPx = box.minY + box.h / 2;
    const w = dPx / canvasW;
    const h = dPx / canvasH;
    const x = Math.max(0.01, Math.min(0.99 - w, cxPx / canvasW - w / 2));
    const y = Math.max(0.01, Math.min(0.99 - h, cyPx / canvasH - h / 2));
    return { shape: 'circle', x, y, w, h };
  }

  // Rounded rectangle from the bounding box.
  let w = box.w / canvasW + padF * 2;
  let h = box.h / canvasH + padF * 2;
  w = Math.max(0.1, Math.min(0.88, w));
  h = Math.max(0.1, Math.min(0.88, h));
  const x = Math.max(0.01, Math.min(0.99 - w, box.minX / canvasW - padF));
  const y = Math.max(0.01, Math.min(0.99 - h, box.minY / canvasH - padF));
  return { shape: 'rect', x, y, w, h };
}

// Build a smooth SVG path string from raw pixel points (for the live sketch trail).
export function strokePath(points) {
  if (!points || points.length === 0) return '';
  let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i].x.toFixed(1)} ${points[i].y.toFixed(1)}`;
  }
  return d;
}

// A tidy grid slot (normalized) for beds that have no saved layout yet.
export function fallbackLayout(index) {
  const cols = 4;
  const col = index % cols;
  const row = Math.floor(index / cols);
  const w = 0.2, h = 0.28, gapX = 0.04, gapY = 0.06;
  return {
    shape: 'rect',
    x: 0.04 + col * (w + gapX),
    y: 0.06 + row * (h + gapY),
    w,
    h,
  };
}

// --- Auto-tidy: keep the canvas from ever looking cluttered ---

// Axis-aligned bounding-box overlap test on normalized layouts (small tolerance
// so beds that merely touch at a grid gap aren't flagged).
export function boxesOverlap(a, b, tol = 0.012) {
  return a.x + a.w > b.x + tol && b.x + b.w > a.x + tol && a.y + a.h > b.y + tol && b.y + b.h > a.y + tol;
}

// Does this set of effective layouts look cluttered (any overlap, or a bed
// pushed well outside the canvas)? Oversized-but-tidy beds are left alone.
export function layoutsNeedTidy(layouts) {
  for (let i = 0; i < layouts.length; i++) {
    const l = layouts[i];
    if (!l) continue;
    if (l.x < -0.01 || l.y < -0.01 || l.x + l.w > 1.01 || l.y + l.h > 1.01) return true;
    for (let j = i + 1; j < layouts.length; j++) {
      if (layouts[j] && boxesOverlap(l, layouts[j])) return true;
    }
  }
  return false;
}

// A clean, non-overlapping grid slot that preserves each bed's shape. Circles
// stay round by squaring their pixel box for the given canvas aspect (w/h).
export function tidyCell(index, count, shape = 'rect', aspect = 1.6) {
  const cols = Math.min(4, Math.max(1, Math.ceil(Math.sqrt(count))));
  const rows = Math.ceil(count / cols);
  const mL = 0.04, mT = 0.05, gapX = 0.045, gapY = 0.06;
  const cellW = (1 - mL * 2 - gapX * (cols - 1)) / cols;
  const cellH = Math.min((1 - mT * 2 - gapY * (rows - 1)) / rows, 0.4);
  const col = index % cols, row = Math.floor(index / cols);
  const cx = mL + col * (cellW + gapX);
  const cy = mT + row * (cellH + gapY);
  if (shape === 'circle') {
    let w = Math.min(cellW, cellH / aspect);
    let h = w * aspect;
    if (h > cellH) { h = cellH; w = h / aspect; }
    return { shape: 'circle', x: cx + (cellW - w) / 2, y: cy + (cellH - h) / 2, w, h };
  }
  return { shape: 'rect', x: cx, y: cy, w: cellW, h: cellH };
}
